import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Slider from "react-slick";
import { withTranslation } from "react-i18next";
import { Card, CardDescription, CardHeader, CardTitle } from "../ui/card";

import "css/admin/timeline.scss";
import videoPlaceholder from "resources/video_placeholder.jpg";
import TimelineArrow from "./TimelineArrow";
import { TIME_FORMAT, TIME_FORMAT_SHORT } from "../../constants/dateFormat";

const SLIDER_SETTINGS = {
  speed: 500,
  infinite: false,
  slidesToShow: 5,
  slidesToScroll: 5,
  nextArrow: <TimelineArrow direction="right" />,
  prevArrow: <TimelineArrow direction="left" />,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 4,
      },
    },
    {
      breakpoint: 1000,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 720,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },
    {
      breakpoint: 360,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

export function LiveTimeline({ itemList, maxItems, onClick, t, language }) {
  moment.locale(language);

  const utcToLocalTime = (time) => moment.utc(time).local();

  const isInThePast = (item) => {
    const { startDate, duration } = item;

    // Checks if the item/live has ended
    return utcToLocalTime(startDate)
      .add(duration, "seconds")
      .isBefore(moment());
  };

  // Filters out past items, just in case.
  // Sort by date, ascending.
  // Slice to maxItems - do we really need a maximum ?
  const chronologicalFutureItemList = itemList
    .filter((media) => !isInThePast(media))
    .sort((a, b) =>
      moment(a.startDate).isBefore(moment(b.startDate)) ? -1 : 1,
    )
    .slice(0, maxItems);

  const renderItems = chronologicalFutureItemList.map((item) => {
    const { uuid, startDate, maxQuality, label, duration, status, picture } =
      item;

    const mediaPicture = picture || videoPlaceholder;
    const durationString = moment
      .duration(duration, "seconds")
      .format(TIME_FORMAT);

    const startDateString = utcToLocalTime(startDate).format("ddd Do MMM YYYY");
    const startTimeString = utcToLocalTime(startDate).format(TIME_FORMAT_SHORT);

    const hasLiveTag = status === "live" || status === "ongoing";

    return (
      <div
        key={uuid}
        className="timeline-content-container"
        data-testid="LiveTimeline-item"
      >
        <Card
          className="relative mx-auto w-full max-w-sm cursor-pointer gap-4 rounded-lg pt-0 pb-4"
          onClick={() => onClick(item)}
          data-testid="LiveTimeline-Card"
        >
          <img
            alt="Event cover"
            className="relative aspect-video w-full rounded-t-lg object-cover"
            src={mediaPicture}
          />
          {hasLiveTag ? (
            <div
              className="timeline-live-chip tag-container"
              title={t("liveOngoing")}
              data-testid="LiveTimeline-liveTag"
            >
              live
            </div>
          ) : null}
          {maxQuality ? (
            <div
              className="absolute top-[55%] left-2 rounded bg-white/90 px-1 py-0.5 text-[10px] text-black"
              title={t("maxResolution")}
              data-testid="LiveTimeline-qualityTag"
            >
              {maxQuality}
              <span>p</span>
            </div>
          ) : null}
          {durationString ? (
            <div
              className="absolute top-[52%] right-2 rounded bg-black/80 px-2 py-1 text-xs text-white"
              title={t("duration")}
              data-testid="LiveTimeline-durationTag"
            >
              {durationString}
            </div>
          ) : null}
          <CardHeader className="px-4">
            <CardTitle
              className="overflow-hidden text-ellipsis whitespace-nowrap"
              data-testid="LiveTimeline-CardTitle"
            >
              {label}
            </CardTitle>
            <CardDescription data-testid="LiveTimeline-CardDescription">
              <div className="flex justify-between">
                <div className="text-xs">{startDateString}</div>
                <div className="text-xs">{startTimeString}</div>
              </div>
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  });

  return (
    <div data-testid="LiveTimeline">
      <div className="text-muted-foreground mb-[0.625rem] text-[26px]">
        {t("nextLives")} ({chronologicalFutureItemList.length})
      </div>
      <div className="timeline-container" data-testid="LiveTimeline-Slider">
        <Slider {...SLIDER_SETTINGS}>{renderItems}</Slider>
      </div>
    </div>
  );
}

LiveTimeline.propTypes = {
  itemList: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      startDate: PropTypes.string.isRequired,
      duration: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      maxQuality: PropTypes.string,
      picture: PropTypes.string,
    }),
  ).isRequired,
  language: PropTypes.string,
  maxItems: PropTypes.number,
  t: PropTypes.func.isRequired,
  onClick: PropTypes.func,
};

LiveTimeline.defaultProps = {
  maxItems: 25,
  onClick: () => null,
  language: "fr",
};

export default withTranslation(["editor-content"])(LiveTimeline);
